import classNames from 'classnames';
import React, { useCallback } from 'react';

import { Icon, IconTypes } from '@eika/icon';

import { useCustomSelectContext } from './CustomSelectContext';
import type { CustomSelectButtonProp } from './types';

const CustomSelectButton = ({
    className,
    placeholder,
    locked = false,
    label,
    children,
    ...rest
}: CustomSelectButtonProp) => {
    const { id, open, value, onClose, toggleOpen, onKeyDownSelect, setButtonRef } = useCustomSelectContext();

    const selectKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Esc' || e.key === 'Escape') {
                if (open) {
                    e.stopPropagation();
                }
                onClose();
                return;
            }
            onKeyDownSelect(e);
        },
        [onKeyDownSelect, open, onClose],
    );

    return (
        <button
            {...rest}
            className={classNames('custom-select__button', className, { 'custom-select__open': open })}
            id={id}
            aria-haspopup="listbox"
            aria-expanded={open}
            onKeyDown={selectKeyDown}
            disabled={locked}
            type="button"
            ref={setButtonRef}
            onClick={toggleOpen}
        >
            <span className="custom-select__button-content-wrapper">
                <span
                    className={classNames(
                        'custom-select__button-label',
                        className ? `${className}__label-text` : undefined,
                        { 'custom-select__button-label--no-text': !label },
                    )}
                    id={`custom-select-label-${id}`}
                >
                    {label && <span>{label}</span>}
                </span>
                <span
                    className={classNames(
                        'custom-select__button-main-content',
                        className ? `${className}__button-main-content` : undefined,
                        {
                            'custom-select__button-main-content--selected': !!value,
                        },
                    )}
                >
                    {value ? children : placeholder}
                </span>
            </span>
            <span
                className={classNames(
                    'custom-select__button-select-icon',
                    className ? `${className}__button-select-icon` : undefined,
                )}
            >
                {locked ? <Icon type={IconTypes.PIN_KODE_24} /> : <Icon type={IconTypes.LITEN_PIL_NED} />}
            </span>
        </button>
    );
};

export default CustomSelectButton;
